import React, { ReactElement, useEffect, useRef, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import ReactTooltip from "react-tooltip";
import { QueryParameters } from "@lifeat/shared/system/RouteConstants";
import GA4Event, { GA4EventProperties } from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import LifeAtCTAModal from "@common/LifeAtModal/LifeAtCTAModal/LifeAtCTAModal";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { sendClarityEvent } from "@features/clarity/Clarity";
import { ClarityEvent } from "@features/clarity/ClarityEvents";
import useQueryParam from "@hooks/useQueryParam";
import Styles from "@themes/Styles";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import UserAccountModal from "@user/components/UserAccountModal/UserAccountModal";
import { sendUserVerificationEmail } from "@user/service/UserService";
import MODAL_IMAGE_SRC from "/resources/images/welcome-image.png";

const BUTTON_TEXT_SIGN_UP = "🚀 Sign Up";
const BUTTON_TEXT_UPGRADE = "🚀 Upgrade";
const BUTTON_TEXT_SIGN_UP_MOBILE = "Sign Up";
const BUTTON_TEXT_UPGRADE_MOBILE = "🚀";
const BUTTON_TEXT_VERIFY = "⚠️ Verify Email";
const BUTTON_TEXT_VERIFY_MOBILE = "⚠️📧";
const BUTTON_TEXT_PRO = "🌟 Pro";
const BUTTON_TEXT_PRO_MOBILE = "🌟";

export default function UpgradeButton() {
    const { user, userData }: UserContextType = useUserContext();
    const { showSignInView, hideSignInView, showUpgradeCta, setUpgradeCtaContext }: CallToActionContextType =
        useCallToActionContext();

    const query = useQueryParam();
    const showUpgradeModal = query.get(QueryParameters.ShowUpgrade) === "true";
    useEffect(() => {
        if (showUpgradeModal) {
            setUpgradeCtaContext("default", UpgradeCTASource.UPGRADE_BUTTON);
        }
    }, [showUpgradeModal]);

    // Note: this is to handle the case where a user signs in, but showSignInView is still true
    useEffect(() => {
        if (showSignInView && userData) {
            hideSignInView();
        }
    }, [userData, showSignInView]);

    const [sentVerifyEmail, setSentVerifyEmail] = useState(false);

    let buttonText: string;
    if (user && !user.emailVerified) {
        buttonText = isMobile && !isTablet ? BUTTON_TEXT_VERIFY_MOBILE : BUTTON_TEXT_VERIFY;
    } else if (userData && !userData.proUserInfo?.isProUser) {
        buttonText = isMobile || isTablet ? BUTTON_TEXT_UPGRADE_MOBILE : BUTTON_TEXT_UPGRADE;
    } else if (userData?.proUserInfo?.isProUser) {
        buttonText = isMobile && !isTablet ? BUTTON_TEXT_PRO_MOBILE : BUTTON_TEXT_PRO;
    } else {
        buttonText = isMobile && !isTablet ? BUTTON_TEXT_SIGN_UP_MOBILE : BUTTON_TEXT_SIGN_UP;
    }

    const handleClick = () => {
        if (user && !user.emailVerified) {
            // Button should be displaying "Verify"
            sendUserVerificationEmail(user).then(() => {
                setSentVerifyEmail(true);
                setTimeout(() => setSentVerifyEmail(false), 6000);
            });
        } else if (
            user &&
            userData &&
            (!userData.proUserInfo?.isProUser || userData?.proUserInfo?.freeTrialInfo?.isFreeTrialActive)
        ) {
            setUpgradeCtaContext("default", UpgradeCTASource.UPGRADE_BUTTON);
        }
    };

    const verifyEmailTooltipContent = () => {
        if (sentVerifyEmail) {
            return (
                <p>
                    We just sent your verification email!
                    <br />
                    (Remember to check your spam)
                </p>
            );
        } else {
            return (
                <p>
                    Please click on the verification link in the email we sent you.
                    <br />
                    Don't forget to check your spam!
                </p>
            );
        }
    };

    const logEventForSignInView = useRef(false);
    let renderModal: ReactElement | undefined;
    if (showSignInView) {
        renderModal = <WelcomeCTA isOpen={showSignInView} closeCta={() => hideSignInView()} />;
        // Log an event once if the user clicks on the upgrade button and they aren't signed in.
        if (!logEventForSignInView.current) {
            logEventForSignInView.current = true;
            GA4Service.logEvent(GA4Event.UPGRADE_BUTTON_CLICK, {
                [GA4EventProperties.upgrade_button_content.key]: GA4EventProperties.upgrade_button_content.authenticate,
            });
            sendClarityEvent(ClarityEvent.UPGRADE_CLICK);
        }
    }

    const contents = () => {
        if ((user !== null && userData !== null) || showSignInView || showUpgradeCta) {
            return (
                <>
                    <span data-tip={""} data-for="verify-email-tip">
                        <LifeAtButtonV2
                            onClick={handleClick}
                            className={`upgrade-button`}
                            size={"small"}
                            overrideTextColor={"#FFF"}
                            overrideBackgroundColor={Styles.buttonFillBlue}
                            disableHover={true}
                        >
                            {buttonText}
                        </LifeAtButtonV2>
                    </span>
                    {user && !user.emailVerified && (
                        <ReactTooltip id={`verify-email-tip`} place={"bottom"} getContent={verifyEmailTooltipContent} />
                    )}

                    {renderModal}
                </>
            );
        } else {
            return <UserAccountModal buttonText={buttonText} hideUserIcon={true} />;
        }
    };

    return user && userData?.proUserInfo?.isProUser && !userData?.proUserInfo?.freeTrialInfo?.isFreeTrialActive
        ? null
        : contents();
}

const MODAL_TITLE = "Join for free! ✨";
const MODAL_SUBTITLE = "Create an account to unlock shuffles, timer, video chat, to-do list, and more!";
const MODAL_CREATE_ACCOUNT_BUTTON_LABEL = "Create Account or Sign In";

interface WelcomeCTAProps {
    isOpen?: boolean;
    closeCta?: () => void;
}
function WelcomeCTA(props: WelcomeCTAProps) {
    // The component that declares this WelcomeCTA should determine whether or
    // not to render it.
    const [isOpen, setIsOpen] = useState<boolean>(props.isOpen || true);
    useEffect(() => {
        if (!isOpen && props.closeCta) {
            props.closeCta();
        }
    }, [isOpen]);

    // TODO: split UserAccountModal into 2 components, so we can split the modal part and display the
    //  modal itself without having to have the button as well.
    const tapToSignupButton = <UserAccountModal buttonText={MODAL_CREATE_ACCOUNT_BUTTON_LABEL} />;

    return (
        <LifeAtCTAModal
            ctaTitle={MODAL_TITLE}
            ctaSubtitle={MODAL_SUBTITLE}
            imageSrc={MODAL_IMAGE_SRC}
            actionButtons={tapToSignupButton}
            isOpen={isOpen}
            closeModal={() => setIsOpen(false)}
        />
    );
}
